import useSWR, { useSWRConfig, mutate as globalMutate } from 'swr';
import { orgApi } from '../utils/apiV2';

/**
 * Financial data layer — single source of truth + anti-stale caching.
 *
 * Every financial surface (Dashboard overview, Revenue, P&L, Expenses) reads
 * through these shared hooks so they all dedupe to ONE network request per
 * dataset (SWR caches by key). The backend (`financial_utils.py`) is the real
 * SSOT — these hooks intentionally surface the backend-computed `summary` and
 * never recompute money totals client-side, so two tabs can never disagree.
 *
 * After any write that touches money (expense, booking, refund, gift card),
 * call the function returned by `useInvalidateFinancials()` so every financial
 * surface refreshes together instead of going stale until the next focus.
 */

// ── Canonical cache keys (SHARED across Revenue ⟷ P&L ⟷ Expenses ⟷ Dashboard) ──
export const financialKeys = {
  bookings: (startDate, endDate) => ['financial-bookings', startDate, endDate],
  expenses: (startDate, endDate) => ['financial-expenses', startDate, endDate],
};

// Key prefixes/fragments that identify a financial cache entry. Used to
// match-and-revalidate every financial surface in one shot.
const FINANCIAL_TAGS = [
  'financial-bookings',
  'financial-expenses',
  '/dashboard/stats',
  '/v2/dashboard',
];

const isFinancialKey = (key) => {
  if (Array.isArray(key)) return FINANCIAL_TAGS.includes(key[0]);
  if (typeof key === 'string') return FINANCIAL_TAGS.some((tag) => key.includes(tag));
  return false;
};

// ── Canonical fetchers ──
const fetchFinancialBookings = ([, startDate, endDate]) =>
  orgApi.getFinancialBookings({ startDate, endDate }).then((res) => res.data);

// orgApi.getExpenses already unwraps to res.data → { expenses: [...] }
const fetchExpenses = ([, startDate, endDate]) =>
  orgApi.getExpenses({ startDate, endDate });

// ── Shared hooks ──

/**
 * Revenue & P&L both call this with the same date range → one shared fetch.
 * `summary` is the backend-computed source of truth; trust it for money totals.
 */
export function useFinancialBookings(startDate, endDate) {
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    startDate && endDate ? financialKeys.bookings(startDate, endDate) : null,
    fetchFinancialBookings,
    { keepPreviousData: true }
  );

  return {
    bookings: data?.bookings || [],
    summary: data?.summary || null,
    raw: data,
    isLoading,
    isValidating,
    isError: !!error,
    refresh: mutate,
  };
}

/**
 * Expenses tab & P&L both call this with the same date range → one shared fetch.
 */
export function useExpenses(startDate, endDate) {
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    startDate && endDate ? financialKeys.expenses(startDate, endDate) : null,
    fetchExpenses,
    { keepPreviousData: true }
  );

  return {
    expenses: data?.expenses || [],
    isLoading,
    isValidating,
    isError: !!error,
    refresh: mutate,
  };
}

/**
 * Returns a function that revalidates EVERY financial surface at once
 * (Dashboard stats, financial-bookings, expenses). Call it after any write
 * that affects money so connected tabs never show stale numbers.
 */
export function useInvalidateFinancials() {
  const { mutate } = useSWRConfig();
  return () => mutate((key) => isFinancialKey(key), undefined, { revalidate: true });
}

/**
 * Standalone (non-hook) version of the above — usable from anywhere, including
 * modal write handlers and imperative callbacks. Uses SWR's global `mutate`,
 * which targets the same default cache as the app-level <SWRConfig> (no custom
 * provider is set). Call after booking create / edit / cancel / refund /
 * add-charge / payment-status changes so the financial tabs refresh.
 */
export const invalidateFinancials = () =>
  globalMutate((key) => isFinancialKey(key), undefined, { revalidate: true });
